import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { fetchGitHubReleases, categorizeReleases, generatePlannedReleases } from '../utils/githubApi';
import { Release, Product, ApplicationUrl } from '../types';

interface ApplicationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  products: Product[];
  selectedProductId: string | null;
}

const ApplicationDialog: React.FC<ApplicationDialogProps> = ({ 
  open, 
  onClose, 
  onSave, 
  products,
  selectedProductId 
}) => {
  const [name, setName] = useState('');
  const [productId, setProductId] = useState(selectedProductId || '');
  const [githubRepoUrl, setGithubRepoUrl] = useState('');
  const [githubProjectsUrl, setGithubProjectsUrl] = useState('');
  const [applicationUrls, setApplicationUrls] = useState<ApplicationUrl[]>([{ name: '', url: '' }]);
  const [fetchedReleases, setFetchedReleases] = useState<Release[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const handleUrlChange = (index: number, field: 'name' | 'url', value: string) => {
    const newUrls = [...applicationUrls];
    newUrls[index][field] = value;
    setApplicationUrls(newUrls);
  };

  const handleFetchReleases = async () => {
    if (!githubRepoUrl.trim()) {
      setFetchError('Please enter a GitHub repository URL');
      return;
    }

    setIsFetching(true);
    setFetchError(null);

    try {
      const releases = await fetchGitHubReleases(githubRepoUrl);
      const categorized = categorizeReleases(releases);
      
      // Generate planned releases from GitHub Projects if URL is available
      let allReleases = [...categorized];
      
      if (githubProjectsUrl.trim() && categorized.length > 0) {
        try {
          // Find the current production release version and date
          const currentRelease = categorized.find(r => r.status === 'Current' && r.releaseType === 'Production');
          const currentVersion = currentRelease?.version || categorized[0]?.version || '1.0.0';
          const currentReleaseDate = currentRelease?.releaseDate || categorized[0]?.releaseDate;
          
          // Generate planned releases based on work items
          const plannedReleases = await generatePlannedReleases(
            githubProjectsUrl,
            currentVersion,
            name,
            currentReleaseDate
          );
          
          allReleases = [...categorized, ...plannedReleases];
        } catch (error) {
          console.error('Failed to generate planned releases:', error);
          // Continue with just the fetched releases
        }
      }
      
      setFetchedReleases(allReleases);
      setFetchError(null);
    } catch (error) {
      setFetchError(error instanceof Error ? error.message : 'Failed to fetch releases');
      setFetchedReleases([]);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = () => {
    const filteredUrls = applicationUrls.filter(item => item.url.trim() !== '' || item.name.trim() !== '');
    onSave({
      name,
      productId,
      githubRepoUrl,
      githubProjectsUrl,
      applicationUrls: filteredUrls,
      releases: fetchedReleases
    });
    handleClose();
  };

  const handleClose = () => {
    setName('');
    setProductId(selectedProductId || '');
    setGithubRepoUrl('');
    setGithubProjectsUrl('');
    setApplicationUrls([{ name: '', url: '' }]);
    setFetchedReleases([]);
    setFetchError(null);
    setIsFetching(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          margin: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          width: { xs: 'calc(100% - 32px)', sm: '100%' }
        }
      }}
    >
      <DialogTitle>New Application</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel>Product</InputLabel>
            <Select
              value={productId}
              label="Product"
              onChange={(e) => setProductId(e.target.value)}
            >
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Application Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="GitHub Repository URL"
              value={githubRepoUrl}
              onChange={(e) => setGithubRepoUrl(e.target.value)}
              fullWidth
              placeholder="https://github.com/owner/repo"
              helperText="Enter the main repository URL (e.g., https://github.com/facebook/react)"
              onBlur={handleFetchReleases}
            />
            <Button
              variant="contained"
              onClick={handleFetchReleases}
              disabled={isFetching || !githubRepoUrl.trim()}
              sx={{ mt: { xs: 0, sm: 1 }, minWidth: '120px', height: '56px', width: { xs: '100%', sm: 'auto' } }}
            >
              {isFetching ? <CircularProgress size={24} /> : 'Fetch Releases'}
            </Button>
          </Box>
          
          {fetchError && (
            <Alert severity="error" onClose={() => setFetchError(null)}>
              {fetchError}
            </Alert>
          )}
          
          {fetchedReleases.length > 0 && (
            <Alert severity="success">
              Successfully fetched {fetchedReleases.length} release{fetchedReleases.length !== 1 ? 's' : ''} 
              ({fetchedReleases.filter(r => r.releaseType === 'Production').length} Production, 
              {fetchedReleases.filter(r => r.releaseType === 'Staging').length} Staging)
            </Alert>
          )}
          <TextField
            label="GitHub Projects URL"
            value={githubProjectsUrl}
            onChange={(e) => setGithubProjectsUrl(e.target.value)}
            fullWidth
            placeholder="https://github.com/users/username/projects/1"
          />
          
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Application URLs
          </Typography>
          {applicationUrls.map((urlItem, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
              <TextField
                label={`Name ${index + 1}`}
                value={urlItem.name}
                onChange={(e) => handleUrlChange(index, 'name', e.target.value)}
                fullWidth
                placeholder="Production, Staging, Dev, etc."
              />
              <TextField
                label={`URL ${index + 1}`}
                value={urlItem.url}
                onChange={(e) => handleUrlChange(index, 'url', e.target.value)}
                fullWidth
                placeholder="https://app.example.com"
              />
              {/* Hidden: Delete URL button
              {applicationUrls.length > 1 && (
                <IconButton onClick={() => handleRemoveUrl(index)} color="error">
                  <DeleteIcon />
                </IconButton>
              )}
              */}
            </Box>
          ))}
          {/* Hidden: Add URL button
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddUrl}
            variant="outlined"
            size="small"
          >
            Add URL
          </Button>
          */}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!name.trim() || !productId}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationDialog;
